import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RefundResult } from '@app/orders/models/orders-transaction.model';
import { TreeListRowData } from '@app/orders/models/orders.model';
import { OrdersCancelService } from '@app/orders/services/orders-cancel.service';
import { OrdersConfirmDialogService } from '@app/orders/services/orders-confirm-dialog.service';
import { OrdersProcessFacade } from '@app/orders/services/orders-process.facade';
import { PaymentTransactionService } from '@app/orders/services/payment-transaction.service';
import { RefundPaymentModeService } from '@app/orders/services/refund-payment-mode.service';
import {
  RefundSummaryBuilderService,
  RefundSummaryItem,
} from '@app/orders/services/refund-summary-builder.service';
import {
  OrderItemPaymentResponseDto,
  OrderItemRowTypeEnumResponseDto,
  PaymentModeResponseDto,
  PaymentTypeEnumRequestDto,
} from '@core/services/apiclient';
import { SalesService } from '@sales/services/sales.service';
import { AbstractPaymentModeFormComponent } from '@shared/components/functional/abstract-payment-mode-form/abstract-payment-mode-form.component';
import { CustomerDisplayItem } from '@shared/customer/models/customer-display-item.model';
import { CustomerService } from '@shared/customer/services/customer.service';
import { SalesPaymentModeModel } from '@shared/models/sales-payment-mode.model';
import { Observable, catchError, of, shareReplay, startWith, take, takeUntil } from 'rxjs';
import { PaymentMethodDisplayMode } from '../payment-method-section/payment-method-section.component';

@Component({
  selector: 'app-orders-refund-dialog',
  templateUrl: './orders-refund-dialog.component.html',
  styleUrl: './orders-refund-dialog.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersRefundDialogComponent extends AbstractPaymentModeFormComponent {
  dialogRef = inject(MatDialogRef<OrdersRefundDialogComponent>);
  private readonly selectionData = inject<TreeListRowData[]>(MAT_DIALOG_DATA);
  private readonly salesService = inject(SalesService);
  private readonly customerService = inject(CustomerService);
  private readonly ordersCancelService = inject(OrdersCancelService);
  private readonly refundSummaryBuilderService = inject(RefundSummaryBuilderService);
  private readonly refundPaymentModeService = inject(RefundPaymentModeService);
  private readonly paymentTransactionService = inject(PaymentTransactionService);
  private readonly ordersProcessFacade = inject(OrdersProcessFacade);
  private readonly ordersConfirmDialogService = inject(OrdersConfirmDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private isLoadingPayments = false;
  readonly maxDisplayedLines = 5;
  isAllItemsDisplayed = false;
  selectedPaymentMode: PaymentModeResponseDto | null = null;
  selectedCustomer = signal<CustomerDisplayItem | null>(null);
  refundsPayload: ReturnType<PaymentTransactionService['createRefundPayload']>[] = [];
  availablePaymentModes: PaymentModeResponseDto[] = [];
  isPaymentBreakdown = false;
  paymentModes = signal<SalesPaymentModeModel[]>([]);
  isCustomerStepInProgress = signal<boolean>(false);
  isPaymentStepInProgress = signal<boolean>(false);
  paymentTypes = [PaymentTypeEnumRequestDto.Direct];
  amountToReturn: number;

  private formValidSignal = signal<boolean>(false);

  get cancelPaymentModes(): SalesPaymentModeModel[] {
    return this.paymentModes();
  }

  readonly isConfirmDisabled = computed(() => {
    return (
      !this.formValidSignal() ||
      !this.selectedCustomer() ||
      !this.paymentModes().length ||
      this.isProcessing()
    );
  });

  private readonly isProcessingSignal = signal<boolean>(false);
  readonly isProcessing = computed(() => this.isProcessingSignal());

  readonly selection = signal<TreeListRowData[]>(this.selectionData);
  readonly isSingleSelection = computed(() => (this.selection() ?? []).length === 1);
  readonly paymentMethodDisplayMode = computed<PaymentMethodDisplayMode>(() =>
    this.isSingleSelection() ? 'detailed' : 'simple',
  );
  readonly showCustomerSection = computed(() => this.isSingleSelection());
  readonly showRefundsListTitle = computed(() => this.isSingleSelection());

  constructor() {
    super();
  }

  get allSummaryItems(): RefundSummaryItem[] {
    return this.refundSummaryBuilderService.buildSummaryItems(this.selection() ?? []);
  }

  get totalAmount(): number {
    return this.refundSummaryBuilderService.calculateTotalAmount(this.selection() ?? []);
  }

  get summaryItems(): RefundSummaryItem[] {
    if (this.isAllItemsDisplayed || this.allSummaryItems.length <= this.maxDisplayedLines) {
      return this.allSummaryItems;
    }
    return this.allSummaryItems.slice(0, this.maxDisplayedLines);
  }

  get remainingLines(): number {
    return Math.max(0, this.allSummaryItems.length - this.maxDisplayedLines);
  }

  get hasMoreLines(): boolean {
    return this.allSummaryItems.length > this.maxDisplayedLines;
  }

  summaryLabel = (item: RefundSummaryItem) => item.label;

  onCustomerChange(customer: CustomerDisplayItem): void {
    this.selectedCustomer.set(customer);
    this.applyRefundPayer();
  }

  editCustomer(): void {
    this.isCustomerStepInProgress.set(true);
  }

  editPaymentMode(): void {
    this.isPaymentStepInProgress.set(true);
    this.createForm();
  }

  paymentModeChange(mode: PaymentModeResponseDto | null): void {
    this.selectedPaymentMode = mode;
    if (this.isPaymentBreakdown) return;
    if (!mode) {
      this.refundsPayload = [];
      this.paymentModes.set([]);
      return;
    }
    this.refundsPayload = [this.paymentTransactionService.createRefundPayload(mode, this.totalAmount)];
    if (this.paymentModes().length === 0) {
      this.paymentModes.set([this.paymentTransactionService.createPaymentMode(mode, this.totalAmount)]);
    }
    this.applyRefundPayer();
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false, response: null });
  }

  onConfirm(): void {
    if (this.form) {
      this.refundsPayload = this.paymentTransactionService.buildRefundPayloadFromForm(
        this.form,
        this.selectedCustomer()?.id ?? null,
        this.amountToReturn,
      );
      this.paymentModes.set(this.paymentTransactionService.updatePaymentModesFromForm(this.form));
    }

    if (!this.refundsPayload.length || !this.selection()?.length) {
      return;
    }

    // Remboursement PARTIEL d'une formule valorisée à 0,00 € (abonnement) : avertissement non bloquant
    // avant le remboursement, en cohérence avec le volet (GEN-6319, ticket « … partiel »). Sur validation
    // on poursuit, sinon on abandonne.
    // NB : ici `totalAmount` est fiable (le dialog charge les vrais montants de paiement via initPaymentModes),
    // d'où le test direct — contrairement à l'annulation liste (OrdersCancelService) où la ligne du groupe ne
    // porte pas de montant réservé fiable et où l'on doit re-résoudre via getSubscriptionOrderItems.
    if (this.isFormulaSelection() && this.totalAmount === 0 && this.isPartialFormulaRefund()) {
      this.ordersConfirmDialogService.showZeroAmountFormulaWarning(
        'refund',
        () => this.executeRefund(),
        () => {},
      );
      return;
    }

    this.executeRefund();
  }

  private isFormulaSelection(): boolean {
    return (this.selection() ?? []).some(
      (row) => row.orderItemRowType === OrderItemRowTypeEnumResponseDto.SubscriptionGroup,
    );
  }

  // « Partiel » côté liste : au moins un groupe de souscription dont les places remboursées (payées +
  // éditées) sont strictement moins nombreuses que ses places actives (hors annulées) (GEN-6319).
  private isPartialFormulaRefund(): boolean {
    return (this.selection() ?? [])
      .filter((row) => row.orderItemRowType === OrderItemRowTypeEnumResponseDto.SubscriptionGroup)
      .some((group) => {
        const activeCount = (group.orderItemCount ?? 0) - (group.canceledOrderRowsCount ?? 0);
        const refundedCount = (group.paidOrderRowsCount ?? 0) + (group.editedOrderRowsCount ?? 0);
        return refundedCount < activeCount;
      });
  }

  private executeRefund(): void {
    this.isProcessingSignal.set(true);
    this.ordersProcessFacade
      .refundOrderItemsAndSubscriptions(this.selection(), this.refundsPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: RefundResult) => {
          this.isProcessingSignal.set(false);
          this.dialogRef.close({
            confirmed: result.success,
            response: result.response,
            error: result.error,
          });
        },
      });
  }

  override ngOnInit(): void {
    this.paymentModes$ = this.getPaymentModes();
    this.totalAmount$ = this.getTotalAmount();
    if (!this.isSingleSelection()) {
      this.createForm();
    }
    this.initSelectedCustomer();
    this.initPaymentModes();
  }

  protected getPaymentModes(): Observable<PaymentModeResponseDto[]> {
    return this.salesService
      .getPaymentMode()
      .pipe(shareReplay({ bufferSize: 1, refCount: false }), takeUntil(this.destroy$));
  }

  protected getTotalAmount(): Observable<number> {
    return of(this.totalAmount);
  }

  public createForm(): void {
    const currentPaymentModes = this.paymentModes();
    if (!currentPaymentModes || currentPaymentModes.length === 0) {
      return;
    }
    const initialPaymentRows = currentPaymentModes.map((mode) => {
      return this.formBuilder.group({
        mode: [mode?.paymentMode, Validators.required],
        amount: [mode?.amount, [Validators.required, Validators.min(0)]],
      });
    });
    this.form = this.formBuilder.group(
      {
        rows: this.formBuilder.array(initialPaymentRows),
      },
      {
        validators: [this.hasUnfilledAmountValidator()],
        asyncValidators: [this.paymentModeAmountsSumValidator(this.totalAmount$)],
      },
    );
    this.setupFormStatusSubscription();
  }

  private setupFormStatusSubscription(): void {
    if (!this.form) {
      return;
    }
    this.form.statusChanges
      .pipe(startWith(this.form.status), takeUntil(this.destroy$))
      .subscribe((status) => {
        this.formValidSignal.set(status === 'VALID');
      });
  }

  private initSelectedCustomer(): void {
    if (!this.isSingleSelection()) return;
    const customerId = this.selection()?.[0]?.customer?.id;
    if (!customerId) return;
    this.customerService
      .getCustomerDisplayById(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customer) => {
          this.selectedCustomer.set(customer);
          this.applyRefundPayer();
          this.cdr.markForCheck();
        },
      });
  }

  private applyRefundPayer(): void {
    this.refundsPayload = this.paymentTransactionService.applyRefundPayer(
      this.refundsPayload,
      this.selectedCustomer(),
    );
  }

  private initPaymentModes(): void {
    if (!this.isSingleSelection()) {
      this.paymentModes$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (modes) => {
          this.availablePaymentModes = modes ?? [];
          this.cdr.markForCheck();
        },
      });
      return;
    }

    const orderItemId = this.selection()?.[0]?.id;
    if (!orderItemId) return;

    this.paymentModes$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (paymentModes) => {
        if (this.totalAmount === 0) {
          this.setFreePaymentMode(paymentModes ?? []);
        } else {
          this.loadInitialPaymentMode(paymentModes ?? [], orderItemId, this.totalAmount);
        }
        this.cdr.markForCheck();
      },
    });
  }

  private setFreePaymentMode(paymentModeitems: PaymentModeResponseDto[]): void {
    const result = this.paymentTransactionService.createFreeModeResult(paymentModeitems, this.totalAmount);
    this.availablePaymentModes = result.availableModes;
    this.isPaymentBreakdown = result.isPaymentBreakdown;
    this.selectedPaymentMode = result.availableModes[0];
    this.refundsPayload = result.refundsPayload;
    this.paymentModes.set(result.cancelPaymentModes);
    this.applyRefundPayer();
    if (this.isSingleSelection()) {
      this.createForm();
    }
  }

  private loadInitialPaymentMode(
    paymentModes: PaymentModeResponseDto[],
    orderItemId: number,
    amount: number,
  ): void {
    if (this.isLoadingPayments) {
      return;
    }
    this.isLoadingPayments = true;
    this.ordersCancelService
      .getOrderItemsPayments(orderItemId)
      .pipe(
        take(1),
        catchError(() => {
          this.isLoadingPayments = false;
          this.setFreePaymentMode(paymentModes);
          return of([]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (allOrderPayments) => {
          this.isLoadingPayments = false;
          if (allOrderPayments?.length) {
            this.processLoadedPayments(allOrderPayments, paymentModes);
          } else {
            this.setFreePaymentMode(paymentModes);
          }
          this.cdr.markForCheck();
        },
      });
  }

  private processLoadedPayments(
    allOrderPayments: OrderItemPaymentResponseDto[],
    paymentModes: PaymentModeResponseDto[],
  ): void {
    const result = this.refundPaymentModeService.processOrderPayments(
      allOrderPayments,
      paymentModes,
      this.totalAmount,
    );

    if (!result.availableModes.length) {
      this.setFreePaymentMode(paymentModes);
      return;
    }

    this.availablePaymentModes = result.availableModes;
    this.refundsPayload = result.refundsPayload;
    this.paymentModes.set(result.cancelPaymentModes);
    this.isPaymentBreakdown = result.isPaymentBreakdown;

    const defaultMode = result.availableModes[0];
    this.selectedPaymentMode = defaultMode;
    if (!this.isPaymentBreakdown) {
      this.paymentModeChange(defaultMode);
    } else {
      this.applyRefundPayer();
    }
    if (this.isSingleSelection()) {
      this.createForm();
    }
  }
}
